import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  KoobButton,
  KoobEmptyState,
  KoobInputDateRange,
  KoobInputSegmented,
  KoobListPageLayout,
  KoobPagination,
  KoobSearchBar,
  KoobSkeleton,
} from '@koob/ui';
import AuditLogDetails from './AuditLogDetails';
import {
  actorLabel,
  AuditLogFailureReason,
  AuditLogIcon,
  AuditLogSentence,
  ImpersonationBadge,
  ViaBadge,
} from './AuditLogSummary';
import { auditLogQuery, FILTERS, RESOURCE_FILTERS } from './query';
import { dayKey, groupByDay, resourceName, resourceTypeLabel } from './labels';

const DEFAULT_PER_PAGE = 50;

const formatTime = (value) =>
  new Date(value).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });

/**
 * Audit log of write actions (KD-3854) as a timeline grouped by day: filters,
 * entries, pagination and the details slide-over, shared by the DMC and TO
 * apps. The API decides what each role sees: KOOB admins get IP, user agent and
 * payload, organization admins their organizations' rows without them.
 *
 * Controlled by `params` (URLSearchParams) and `onParamsChange(next)`, so the
 * full page keeps its filters in the URL and a resource view in local state.
 * `fetchAuditLogs(queryString)` resolves to `{ items, total }`. With
 * `filterable` false (resource views), users and resources are plain text,
 * not filter links. The full page shows the total in its page header:
 * `onTotalChange(total)` hands it over.
 */
export default function AuditLogTimeline({
  params,
  onParamsChange,
  fetchAuditLogs,
  actions,
  onTotalChange,
  width = 'full',
  filterable = true,
  defaultPerPage = DEFAULT_PER_PAGE,
  // Off when the host shows the total itself (KD-3842 client folder card).
  showCount = true,
  // Inside another card (the client folder's activity log section): no frame.
  embedded = false,
}) {
  const { t } = useTranslation('auditLog');
  const [state, setState] = useState({ loading: true });
  const [selected, setSelected] = useState(null);
  const [action, setAction] = useState(params.get('action') ?? '');
  const page = parseInt(params.get('page') ?? '1', 10) || 1;
  const perPage = parseInt(params.get('perPage') ?? '', 10) || defaultPerPage;
  const query = auditLogQuery(
    params,
    perPage,
    filterable ? FILTERS : RESOURCE_FILTERS,
  );

  const set = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries({ page: undefined, ...patch }).forEach(([key, value]) =>
      value ? next.set(key, value) : next.delete(key),
    );
    onParamsChange(next);
  };

  useEffect(() => {
    let active = true;
    setState((previous) => ({ ...previous, loading: true }));
    fetchAuditLogs(query)
      .then((data) => active && setState({ data }))
      .catch(() => active && setState({ error: true }));
    return () => {
      active = false;
    };
  }, [fetchAuditLogs, query]);

  // Search as you type (400 ms), no Enter needed.
  useEffect(() => {
    if ((params.get('action') ?? '') === action) {
      return undefined;
    }
    const id = setTimeout(() => set({ action }), 400);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action]);

  const filterButton = (label, patch, className = '') =>
    filterable ? (
      <button
        type="button"
        className={`border-none bg-transparent p-0 text-left hover:underline ${className}`}
        onClick={(event) => {
          event.stopPropagation();
          set(patch);
        }}
      >
        {label}
      </button>
    ) : (
      <span className={className}>{label}</span>
    );

  const items = state.data?.items ?? [];
  // An organization admin sees one organization: naming it on every entry says
  // nothing. Show it only when the entries span several (KOOB admins).
  const showOrganization =
    new Set(items.map((row) => row.organization?.id).filter(Boolean)).size > 1;

  const today = dayKey(new Date());
  const yesterday = dayKey(Date.now() - 24 * 60 * 60 * 1000);
  const dayLabel = (day) =>
    day === today
      ? t('today')
      : day === yesterday
        ? t('yesterday')
        : new Date(`${day}T12:00:00`).toLocaleDateString([], {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: day.slice(0, 4) === today.slice(0, 4) ? undefined : 'numeric',
          });

  const actor = (row) =>
    row.actor
      ? filterButton(
          actorLabel(t, row),
          { actorUserId: row.actor.id },
          'font-medium text-gray-700',
        )
      : actorLabel(t, row);

  const entry = (row) => {
    return (
      <li
        key={row.id}
        className="relative pb-1 pl-10 before:absolute before:-bottom-2.5 before:left-4 before:top-10 before:w-px before:bg-gray-200 last:before:hidden"
      >
        <AuditLogIcon row={row} className="absolute left-0 top-2.5" />
        <button
          type="button"
          onClick={() => setSelected(row)}
          className="flex w-full flex-col gap-1 rounded-lg border-none bg-transparent px-3 py-2.5 text-left hover:bg-gray-50"
        >
          <AuditLogSentence
            row={row}
            name={filterButton(
              resourceName(row),
              { resourceType: row.resourceType, resourceId: row.resourceId },
              'font-medium text-gray-900',
            )}
          />
          <AuditLogFailureReason row={row} />
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500">
            <span>{actor(row)}</span>
            <ImpersonationBadge row={row} />
            <ViaBadge row={row} />
            {showOrganization && row.organization && !row.koobSupport && (
              <>
                <span aria-hidden>·</span>
                {filterButton(row.organization.name, {
                  organizationId: row.organization.id,
                })}
              </>
            )}
            <span aria-hidden>·</span>
            <time dateTime={row.occurredAt}>{formatTime(row.occurredAt)}</time>
          </span>
        </button>
      </li>
    );
  };

  const pinned = [
    params.get('actorUserId') && {
      label: t('filters.user', { id: params.get('actorUserId') }),
      clear: { actorUserId: undefined },
    },
    params.get('organizationId') && {
      label: t('filters.organization', { id: params.get('organizationId') }),
      clear: { organizationId: undefined },
    },
    params.get('resourceType') && {
      label: [
        resourceTypeLabel(t, params.get('resourceType')),
        params.get('resourceId') && `#${params.get('resourceId')}`,
      ]
        .filter(Boolean)
        .join(' '),
      clear: { resourceType: undefined, resourceId: undefined },
    },
  ].filter(Boolean);

  const from = params.get('from');
  const to = params.get('to');
  const total = state.data?.total ?? 0;
  const loaded = Boolean(state.data);

  useEffect(() => {
    if (loaded) {
      onTotalChange?.(total);
    }
  }, [loaded, total, onTotalChange]);

  const body = () => {
    if (state.error) {
      return <KoobEmptyState title={t('loadError')} />;
    }
    if (!state.data) {
      return (
        <div className="flex justify-center py-12">
          <KoobSkeleton shape="rows" />
        </div>
      );
    }
    if (!items.length) {
      return <KoobEmptyState title={t('empty')} description={t('retention')} />;
    }
    return (
      <div className={state.loading ? 'opacity-50 transition-opacity' : ''}>
        <ol className="flex flex-col gap-6">
          {groupByDay(items).map(({ day, rows }) => (
            <li key={day}>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                {dayLabel(day)}
              </h3>
              <ol>{rows.map(entry)}</ol>
            </li>
          ))}
        </ol>
      </div>
    );
  };

  return (
    <KoobListPageLayout
      width={width}
      embedded={embedded}
      footer={
        loaded &&
        !state.error &&
        items.length > 0 && (
          <KoobPagination
            embedded
            page={page}
            setPage={(next) => set({ page: next > 1 ? String(next) : '' })}
            totalCount={total}
            totalPages={Math.max(1, Math.ceil(total / perPage))}
            perPage={perPage}
            setPerPage={(next) => set({ perPage: String(next) })}
          />
        )
      }
      toolbarActions={
        (actions || (showCount && loaded)) && (
          <>
            {showCount && loaded && (
              <span className="text-sm text-gray-500">
                {t('subtitle', { count: total })}
              </span>
            )}
            {actions}
          </>
        )
      }
      search={
        <KoobSearchBar
          rounded="md"
          placeholder={t('filters.action')}
          value={action}
          onChange={(value) => setAction(value)}
        />
      }
      filters={
        <>
          <KoobInputSegmented
            value={params.get('outcome')}
            onChange={(value) => set({ outcome: value })}
            options={[
              { value: null, label: t('filters.allOutcomes') },
              ...['success', 'denied', 'error'].map((value) => ({
                value,
                label: t(`outcomes.${value}`),
              })),
            ]}
          />
          <KoobInputDateRange
            placeholder={t('filters.period')}
            value={
              from
                ? { from: new Date(from), to: to ? new Date(to) : undefined }
                : null
            }
            onChange={(range) =>
              set({
                from: range?.from?.toISOString(),
                to: range?.to?.toISOString(),
              })
            }
          />
          {pinned.map((filter) => (
            <KoobButton
              key={filter.label}
              size="sm"
              variant="soft"
              onClick={() => set(filter.clear)}
            >
              {filter.label}
              <i className="fa-regular fa-xmark ml-1" />
            </KoobButton>
          ))}
        </>
      }
    >
      {body()}
      <AuditLogDetails
        log={selected}
        onClose={() => setSelected(null)}
        showOrganization={showOrganization}
      />
    </KoobListPageLayout>
  );
}
